import { useMemo, useState } from "react";
import { TabSection } from "@frontend/lyng/tabSection/TabSection";
import { TopButton } from "../../../components/core/topButton/TopButton";
import { NotesList } from "../NotesList";
import { PastVisits } from "@frontend/lyng/assets/icons/24/outline";
import { Phone, Careplan } from "@frontend/lyng/assets/icons/24/filled";
import { useNavigate, useParams } from "react-router-dom";
import { Label } from "@frontend/lyng/typography/label/Label";
import { ShiftActivities } from "../../../components/core/activities/ShiftActivities";
import { useVisitInfo } from "../../../utils/visits";
import {
  ActivityStatus,
  useCareRecipientByIdQuery,
  useVisitByIdQuery,
} from "../../../api/generated/graphql";
import { useCareContext } from "../../../providers";
import { useSortingOptions } from "@frontend/lyng/utils/hooks/useSortingOptions";
import { useTranslate } from "@tolgee/react";

export const CareRecipientDetails = () => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState("activities");
  const { id, careRecipientId } = useParams();
  const {
    state: { viewer },
  } = useCareContext();
  const { nameOrderFn, collator } = useSortingOptions(viewer?.tenantSettings);

  const tabs = useMemo(
    () => [
      { id: "activities", label: t("shiftDetails.activities.title") },
      { id: "notes", label: t("shiftDetails.visitNotes.title") },
    ],
    [t],
  );

  const { data } = useVisitByIdQuery({
    variables: {
      visitInstanceId: id ?? "",
    },
    skip: !id,
  });

  const subject = data?.visitById?.subject;

  const { data: careRecipient } = useCareRecipientByIdQuery({
    variables: {
      id: careRecipientId ?? "",
    },
    skip: !careRecipientId,
  });

  const shift = useVisitInfo(data?.visitById);

  const visitStatus = useMemo(() => {
    return shift?.status ?? "pending";
  }, [shift]);

  const onlyActivitiesForCareRecipient = useMemo(
    () =>
      shift?.activities.filter(
        (activity) => activity.subject.id === careRecipientId,
      ),
    [shift?.activities, careRecipientId],
  );

  const dueActivities = useMemo(
    () =>
      onlyActivitiesForCareRecipient
        ?.filter(
          (activity) =>
            activity.isDue || activity.status === ActivityStatus.Completed,
        )
        .sort((a, b) => {
          return collator.compare(a.title, b.title);
        }),
    [onlyActivitiesForCareRecipient, collator],
  );

  const nonDueActivitiesCount = useMemo(() => {
    return onlyActivitiesForCareRecipient?.filter(
      (activity) =>
        !activity.isDue && activity.status !== ActivityStatus.Completed,
    ).length;
  }, [onlyActivitiesForCareRecipient]);

  const onlyNotesForCareRecipient = useMemo(
    () =>
      data?.visitById?.visitNotes.filter(
        (note) => note.careRecipient?.id === careRecipientId,
      ),
    [data?.visitById?.visitNotes, careRecipientId],
  );

  if (!careRecipientId || !id) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex flex-col h-screen bg-main">
      <div className="absolute top-10 px-20">
        <div className="flex flex-col pt-2">
          <Label size="m">
            {nameOrderFn(
              careRecipient?.careRecipientById ?? {
                firstName: null,
                lastName: null,
              },
            )}
          </Label>
        </div>
      </div>

      <div className="mt-4 flex gap-2 z-20 px-2 py-4">
        <TopButton
          text={t("contacts.title")}
          icon={<Phone className=" text-secondary-500" />}
          onClick={() => navigate(`./contacts`)}
        />

        <TopButton
          text={t("carePlan.title")}
          icon={<Careplan className="text-secondary-500" />}
          onClick={() => navigate("./careplan")}
        />

        <TopButton
          text={t("pastVisits")}
          icon={<PastVisits className="text-secondary-500" />}
          onClick={() => navigate(`./past-visits/${subject?.id}`)}
        />
      </div>

      <div className="sticky top-0 z-10 bg-main">
        <TabSection
          tabs={tabs}
          currentTab={currentTab}
          onChange={setCurrentTab}
          theme="mobile"
          variant="leading"
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {currentTab === "activities" && (
          <div className="my-10 pb-10">
            <ShiftActivities
              displayView={false}
              activities={dueActivities ?? []}
              visitStatus={visitStatus}
              visitInstanceId={id}
              nonDueActivitiesCount={nonDueActivitiesCount ?? 0}
            />
          </div>
        )}
        {currentTab === "notes" && (
          <div className="pb-24">
            <NotesList
              notes={onlyNotesForCareRecipient ?? []}
              hasClockedIn={visitStatus === "clockedIn"}
            />
          </div>
        )}
      </div>
    </div>
  );
};
